import { useEffect } from 'react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
  RouterProvider,
} from 'react-router-dom';
import { CommandStandaloneLayout } from './layouts/CommandStandaloneLayout';
import StoreLayout from './layouts/StoreLayout';
import AllOrganizationsPage from './pages/AllOrganizationsPage';
import AllPricingSetsPage from './pages/AllPricingSetsPage';
import { AllShippingClaimsPage } from './pages/AllShippingClaimsPage';
import AllStoreAlertsPage from './pages/AllStoreAlertsPage';
import AllStoresPage from './pages/AllStoresPage';
import AllUsersPage from './pages/AllUsersPage';
import { DashboardsPage } from './pages/dashboards/DashboardsPage';
import OrganizationPage from './pages/organization/OrganizationPage';
import PricingSetDetailPage from './pages/pricingSet/PricingSetPage';

import OnboardingPage from './pages/store/OnboardingPage';
import PlatformPage from './pages/store/platform/PlatformPage';
import ShippingProtectionPage from './pages/store/shippingProtection/ShippingProtectionPage';
import StoreBillingPage from './pages/store/storeBilling/StoreBillingPage';
import StoreInfoPage from './pages/store/storeInfo/StoreInfoPage';
import StorePricingSetPage from './pages/store/StorePricingSetPage';
import StoreUsersPage from './pages/store/StoreUsersPage';
import SupportSlaPage from './pages/store/SupportSlaPage';
import SidekickConversationPage from './pages/sidekick/SidekickConversationPage';
import SidekickConversationsPage from './pages/sidekick/SidekickConversationsPage';
import UnhandledError from './pages/UnhandledErrorPage';
import UserDetailPage from './pages/user/UserDetailPage';
import Auth0ProviderWithNavigate from './providers/Auth0ProviderWithNavigate';
import Auth0UserIntoAuthProvider from './providers/AuthProvider';
import { StoreProvider } from './providers/StoreProvider';

function MarkReady() {
  useEffect(() => {
    const root = document.getElementById('root');
    if (!root) {
      console.error('#root Element Missing');
      return;
    }

    root.dataset.ready = 'ready';
  }, []);

  return null;
}

const routes = createRoutesFromElements(
  <Route
    errorElement={<UnhandledError />}
    path="/"
    element={
      <Auth0ProviderWithNavigate>
        <Auth0UserIntoAuthProvider>
          <StoreProvider>
            <CommandStandaloneLayout>
              <MarkReady />
              <Outlet />
            </CommandStandaloneLayout>
          </StoreProvider>
        </Auth0UserIntoAuthProvider>
      </Auth0ProviderWithNavigate>
    }
  >
    <Route index element={<Navigate to="/stores" replace />} />

    <Route path="/pricing-sets">
      <Route index element={<AllPricingSetsPage />} />
      <Route path=":setId" element={<PricingSetDetailPage />} />
    </Route>

    <Route path="/stores" element={<AllStoresPage />} />

    <Route path="/dashboards/:group?" element={<DashboardsPage />} />

    <Route path="/store-alerts" element={<AllStoreAlertsPage />} />

    <Route path="/sidekick">
      <Route index element={<SidekickConversationsPage />} />
      <Route path=":conversationId" element={<SidekickConversationPage />} />
    </Route>

    <Route path="/shipping-claims" element={<AllShippingClaimsPage />} />

    <Route path="/organizations">
      <Route index element={<AllOrganizationsPage />} />
      <Route path=":organizationId" element={<OrganizationPage />} />
    </Route>

    <Route path="/users">
      <Route index element={<AllUsersPage />} />
      <Route path=":userId" element={<UserDetailPage />} />
    </Route>

    <Route path="/stores/:storeId" element={<StoreLayout />}>
      <Route index element={<Navigate to="info" replace />} />
      <Route path="info" element={<StoreInfoPage />} />
      <Route path="billing" element={<StoreBillingPage />} />
      <Route path="users" element={<StoreUsersPage />} />
      <Route path="pricing-set" element={<StorePricingSetPage />} />
      <Route path="shipping-protection" element={<ShippingProtectionPage />} />

      <Route path="onboarding" element={<OnboardingPage />} />
      <Route path="support-sla" element={<SupportSlaPage />} />
      <Route path="platform" element={<PlatformPage />} />
    </Route>
  </Route>,
);

export default function Router() {
  const router = createBrowserRouter(routes);

  return <RouterProvider router={router} />;
}
